import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  OnInit,
  Input,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  AfterViewInit,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';

import { SnDVRStream } from '@lib/api/entities';

import { Timeline } from '../../classes/timeline';
import { ITimeline } from '../../interfaces';

@Component({
  selector: 'fs-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input()
  public timelineDefinition: any;

  @Input()
  public currentDate: Date = new Date();

  @Input()
  public currentTime: Date = new Date();

  @Input()
  public height: number;

  @Input()
  public disableThumnail: boolean;

  @Output()
  public readonly brush = new EventEmitter();

  @Output()
  public readonly dragging = new EventEmitter<boolean>();

  @Output()
  public readonly moveCursor = new EventEmitter<Date>();

  @Output()
  public readonly segment = new EventEmitter<ITimeline>();

  @Output()
  public readonly zoom = new EventEmitter<d3.ZoomTransform>();

  @Output()
  public readonly userSeeking = new EventEmitter<boolean>();

  @Input()
  public prerecordingUrls: SnDVRStream;

  @Input()
  public fullscreen: boolean = false;

  public tooltipText: string = '';
  public tooltipPosition = { x: 0, y: 0 };
  public showTooltip: boolean = false;

  private _resizeTimeout: any;
  private _timelineRef: Timeline;

  private readonly _destroy$ = new Subject();

  get timeline(): Timeline {
    return this._timelineRef;
  }

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    if (this.timelineDefinition) {
      const timelineDef = this.timelineDefinition.record
        ? this.timelineDefinition
        : [this.timelineDefinition];
      const height = this.height ? this.height : 64;
      this._timelineRef = new Timeline(
        this._elementRef,
        this.currentDate,
        timelineDef,
        height,
        this.disableThumnail,
        this.fullscreen,
        this,
      );

      this._timelineRef.brush$
        .pipe(takeUntil(this._destroy$))
        .subscribe((data) => this.brush.next(data));

      this._timelineRef.dragging$.pipe(takeUntil(this._destroy$)).subscribe((dragging) => {
        if (this.prerecordingUrls) {
          this._timelineRef.currentThumbnail = this.prerecordingUrls;
        }
        this.dragging.next(dragging);
      });

      this._timelineRef.moveCursor$
        .pipe(takeUntil(this._destroy$))
        .subscribe((date) => this.moveCursor.next(date));

      this._timelineRef.segment$
        .pipe(takeUntil(this._destroy$))
        .subscribe((segment) => this.segment.next(segment));

      this._timelineRef.zoom$
        .pipe(takeUntil(this._destroy$))
        .subscribe((zoom) => this.zoom.next(zoom));

      this._timelineRef.userSeeking$
        .pipe(takeUntil(this._destroy$))
        .subscribe((userSeeking) => this.userSeeking.next(userSeeking));
    }
  }

  public ngOnDestroy(): void {
    if (this._timelineRef) {
      this._timelineRef.destroy();
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  public updateTooltip(event: MouseEvent, timeText: string): void {
    if (!this.fullscreen) {
      this.hideTooltip();
      return;
    }

    this.tooltipText = timeText;
    this._calculateTooltipPosition(event);
    this.showTooltip = true;
    this._changeDetectorRef.detectChanges();
  }

  private _calculateTooltipPosition(event: MouseEvent): void {
    const rect = this._elementRef.nativeElement.getBoundingClientRect();
    const tooltipWidth = 80;
    const tooltipHeight = 26;
    const margin = 4;
    const yPos = -tooltipHeight / 2;
    
    const centeredX = event.clientX - rect.left - tooltipWidth / 2;
    const maxX = rect.width - tooltipWidth - margin;
    const xPos = Math.max(-15, Math.min(centeredX, maxX));
    
    this.tooltipPosition = { x: xPos, y: yPos };
  }

  public hideTooltip(): void {
    this.showTooltip = false;
    this.tooltipText = '';
    this._changeDetectorRef.detectChanges();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (this._resizeTimeout) {
      clearTimeout(this._resizeTimeout);
    }
    this._resizeTimeout = setTimeout(() => {
      this._timelineRef.redraw();
    }, 500);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this._timelineRef) {
      if (changes.currentTime) {
        this._timelineRef.currentTime = this.currentTime;
        const timelineDef = this.timelineDefinition.record
          ? this.timelineDefinition
          : [this.timelineDefinition];
        this._timelineRef.data = timelineDef;
      }
      if (changes.currentDate) {
        this._timelineRef.currentDate = this.currentDate;
        this._timelineRef.redraw();
      }
      if (changes.fullscreen) {
        this._timelineRef.fullscreen = this.fullscreen;
      }
    }
  }

  public ngAfterViewInit(): void {
    if (this._timelineRef) {
      this._timelineRef.redraw();
    }
    this._changeDetectorRef.detectChanges();
  }

  public setZoom(zoom: d3.ZoomTransform): void {
    this._timelineRef.setZoom(zoom);
  }

  public nextIfAvailable(): void {
    this._timelineRef.nextIfAvailable();
  }

  public deactivateSegment(): void {
    this._timelineRef.deactivateSegment();
  }

  public redraw(): void {
    this._timelineRef.redraw();
  }

  public resetTransform(): void {
    this._timelineRef.resetTransform();
  }

  public update(): void {
    this._timelineRef.updateTimeline();
  }
}
